export const MAX_HEATMAP_BINS = 10_000;

export type HeatmapGridAxis = { min: number; max: number; step: number };

// Snap arithmetic noise at a boundary without rounding measurements to a fixed precision.
const getAxisPosition = (value: number, min: number, step: number) => {
  const position = (value - min) / step;
  const nearest = Math.round(position);
  const tolerance = Number.EPSILON * 16 * Math.max(1, Math.abs(position));
  return value === min + nearest * step || Math.abs(position - nearest) <= tolerance ? nearest : position;
};

export function getAxisBinCount({ min, max, step }: HeatmapGridAxis): number {
  if (![min, max, step].every(Number.isFinite) || min >= max || step <= 0 || min + step === min) {
    return 0;
  }
  const count = Math.ceil(getAxisPosition(max, min, step));
  return count > 0 && count <= MAX_HEATMAP_BINS ? count : 0;
}

export function isValidHeatmapGrid(x: HeatmapGridAxis, y: HeatmapGridAxis) {
  const count = getAxisBinCount(x) * getAxisBinCount(y);
  return count > 0 && count <= MAX_HEATMAP_BINS;
}

export function buildAxisValues(min: number, max: number, step: number) {
  const count = getAxisBinCount({ min, max, step });
  return Array.from({ length: count }, (_, index) => (index === count - 1 ? max : min + (index + 1) * step));
}

export function getAxisBinIndex(value: number, axis: HeatmapGridAxis, count = getAxisBinCount(axis)) {
  if (!Number.isFinite(value) || value < axis.min || value >= axis.max) {
    return -1;
  }
  return count ? Math.min(Math.floor(getAxisPosition(value, axis.min, axis.step)), count - 1) : -1;
}

export function valueToCategoryCoordinate(value: number, axis: HeatmapGridAxis) {
  const count = getAxisBinCount(axis);
  if (!count || !Number.isFinite(value)) {
    return NaN;
  }
  const index = Math.max(0, Math.min(getAxisBinIndex(value, axis), count - 1));
  const cellIndex = value >= axis.max ? count - 1 : index;
  const lower = axis.min + cellIndex * axis.step;
  const upper = cellIndex === count - 1 ? axis.max : lower + axis.step;
  return cellIndex - 0.5 + (value - lower) / (upper - lower);
}
